/**
 * AnsiText — composant réutilisable pour afficher du texte contenant des
 * séquences d'échappement ANSI (couleurs, gras, reset…).
 *
 * Utilise `ansi-to-html` pour la conversion et `dompurify` pour la sanitisation.
 *
 * Usage :
 *   <AnsiText sx={{ maxHeight: 400, overflow: "auto" }}>
 *     {rawLogString}
 *   </AnsiText>
 *
 *   // Avec ref pour auto-scroll :
 *   const ref = React.useRef(null);
 *   <AnsiText ref={ref} placeholder="Chargement…">{logs}</AnsiText>
 */
import React from "react";
import { Box } from "@mui/material";
import AnsiToHtml from "ansi-to-html";
import DOMPurify from "dompurify";

// Palette adaptée au thème sombre GitHub-like
const ansiConverter = new AnsiToHtml({
    escapeXML: true,
    newline: false,
    colors: {
        0: "#c9d1d9",
        1: "#ff7b72",   // rouge  — ERROR
        2: "#3fb950",   // vert   — SUCCESS
        3: "#d29922",   // jaune  — WARNING
        4: "#79c0ff",   // bleu   — INFO
        5: "#bc8cff",   // violet
        6: "#39c5cf",   // cyan
        7: "#b1bac4",   // gris clair
    },
});

const DEFAULT_SX = {
    display: "block",
    px: 2, py: 1.5, m: 0,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
    fontSize: 11.5,
    lineHeight: 1.65,
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    overflow: "auto",
    bgcolor: "#0d1117",
    color: "#c9d1d9",
};

// ── Pré-processeur ESC ────────────────────────────────────────────────────────
/**
 * Certains backends (ex. Kaniko via Spring) stockent les logs ANSI sans le
 * caractère ESC (0x1B), ne gardant que la partie `[36m`. Ce helper le réinjecte
 * pour que ansi-to-html puisse faire son travail.
 *
 * Règle : on ajoute \x1b uniquement devant les séquences SGR `[<nums>m`
 * (ex. [36m, [0m, [1;32m) — PAS devant les timestamps Kaniko `[0001]` dont
 * le dernier caractère est `]` et non `m`.
 */
const ESC = "\x1b";
const HAS_ESC_RE  = /\x1b\[/;
const SGR_BARE_RE = /\[(\d+(?:;\d+)*)m/g;   // [ digits m  sans ESC devant

function normalizeAnsi(text) {
    if (!text) return text;
    if (HAS_ESC_RE.test(text)) return text;          // ESC déjà présent → ok
    return text.replace(SGR_BARE_RE, `${ESC}[$1m`); // réinjection
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * @param {object}  props
 * @param {string}  [props.children]    — texte brut pouvant contenir des codes ANSI
 * @param {string}  [props.placeholder] — texte affiché si children est null/vide
 * @param {object}  [props.sx]          — surcharge du style MUI sx
 */
const AnsiText = React.forwardRef(function AnsiText({ children, placeholder, sx }, ref) {
    const html = React.useMemo(() => {
        const raw = children == null ? "" : String(children);
        const source = raw || (placeholder ?? "");
        if (!source) return "";
        try {
            return DOMPurify.sanitize(ansiConverter.toHtml(normalizeAnsi(source)));
        } catch {
            return DOMPurify.sanitize(source);
        }
    }, [children, placeholder]);

    return (
        <Box
            component="pre"
            ref={ref}
            sx={{ ...DEFAULT_SX, ...sx }}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
});

export default AnsiText;

